import { barFill, brand, FillBar } from '@alison/ui';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { chartValueText, rowValueText } from './chartFormat.js';
import type { InsightsChart } from './reportResult.js';

export const VIOLET_TINT = alpha(brand.violet, 0.1);
const MUTED_VIOLET = alpha(brand.violet, 0.45);
const TRACK_SX = { bgcolor: VIOLET_TINT };
const LABEL_WIDTH = 190;
const GAUGE_WIDTH = 44;
const GAUGE_HEIGHT = 88;
const BAR_HEIGHT = 8;

type Row = InsightsChart['rows'][number];

function largest(rows: Row[]): number {
  return Math.max(...rows.map((row) => Math.abs(row.value)), Number.EPSILON);
}

// A row the service judged a step back reads muted; one it did not judge stays violet.
function barColor(row: Row): string {
  return row.is_improvement === false ? MUTED_VIOLET : brand.violet;
}

// A chart with a fall diverges from a centre zero line, so the side of the bar carries the
// sign even where the service gives no verdict; the colour stays the service's verdict.
function SignedBar({
  row,
  max,
  unit,
}: {
  row: Row;
  max: number;
  unit: InsightsChart['unit'];
}): ReactNode {
  const percent = Math.round(row.value * 100);
  const reach = Math.round(max * 100);
  return (
    <Box
      role="meter"
      aria-label={row.label}
      aria-valuenow={percent}
      aria-valuemin={-reach}
      aria-valuemax={reach}
      aria-valuetext={rowValueText(unit, row.value)}
      sx={(theme) => ({
        position: 'relative',
        flex: 1,
        height: BAR_HEIGHT,
        borderRadius: `${theme.radius.pill}px`,
        ...TRACK_SX,
      })}
    >
      <Box
        data-testid="signed-bar-fill"
        sx={(theme) => ({
          position: 'absolute',
          top: 0,
          bottom: 0,
          ...(row.value < 0 ? { right: '50%' } : { left: '50%' }),
          width: `${(Math.abs(row.value) / max) * 50}%`,
          borderRadius: `${theme.radius.pill}px`,
          background: barFill(barColor(row)),
        })}
      />
      <Box
        data-testid="signed-bar-zero"
        sx={{
          position: 'absolute',
          top: -2,
          bottom: -2,
          left: '50%',
          width: '1px',
          bgcolor: 'text.secondary',
        }}
      />
    </Box>
  );
}

function ChartCard({ chart, children }: { chart: InsightsChart; children: ReactNode }): ReactNode {
  return (
    <Stack
      data-testid={`insights-chart-${chart.key}`}
      sx={{ minWidth: 0, gap: 3, p: 4, borderRadius: 1.2, bgcolor: 'background.page' }}
    >
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{chart.title}</Typography>
        {chart.subtitle === '' ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{chart.subtitle}</Typography>
        )}
      </Box>
      {children}
    </Stack>
  );
}

function Caption({ children }: { children: ReactNode }): ReactNode {
  return (
    <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

function Bars({ chart }: { chart: InsightsChart }): ReactNode {
  const max = largest(chart.rows);
  const signed = chart.rows.some((row) => row.value < 0);
  return (
    <Stack sx={{ gap: 2 }}>
      {chart.rows.map((row, index) => (
        <Stack
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, ordered list; two rows may share a label
          key={index}
          direction="row"
          sx={{ gap: 2, alignItems: 'center' }}
        >
          <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }} noWrap title={row.label}>
              {row.label}
            </Typography>
            {row.detail === '' ? null : (
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap title={row.detail}>
                {row.detail}
              </Typography>
            )}
          </Box>
          {signed ? (
            <SignedBar row={row} max={max} unit={chart.unit} />
          ) : (
            <FillBar
              label={row.label}
              valueText={rowValueText(chart.unit, row.value)}
              percent={(Math.abs(row.value) / max) * 100}
              color={barColor(row)}
              height={BAR_HEIGHT}
              sx={[{ flex: 1 }, TRACK_SX]}
            />
          )}
          <Typography
            sx={{ minWidth: 48, flexShrink: 0, fontSize: 12, fontWeight: 600, textAlign: 'right' }}
          >
            {rowValueText(chart.unit, row.value)}
          </Typography>
        </Stack>
      ))}
      {chart.baseline == null ? null : <Caption>{chartValueText(chart.baseline)}</Caption>}
    </Stack>
  );
}

function Share({ chart }: { chart: InsightsChart }): ReactNode {
  const [row] = chart.rows;
  if (row === undefined) return null;
  return (
    <Stack sx={{ gap: 2 }}>
      <Typography sx={{ fontSize: 36, fontWeight: 600, lineHeight: 'normal', color: brand.violet }}>
        {rowValueText(chart.unit, row.value)}
      </Typography>
      <FillBar
        label={row.label}
        percent={row.value * 100}
        color={brand.violet}
        height={10}
        sx={TRACK_SX}
      />
      {chart.stats.map((stat) => (
        <Caption key={stat.metric}>{chartValueText(stat)}</Caption>
      ))}
    </Stack>
  );
}

function Columns({ chart }: { chart: InsightsChart }): ReactNode {
  const max = largest(chart.rows);
  return (
    <Stack direction="row" sx={{ gap: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {chart.rows.map((row, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, ordered list; two rows may share a label
        <Stack key={index} sx={{ gap: 1, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {rowValueText(chart.unit, row.value)}
          </Typography>
          <Box
            role="img"
            aria-label={row.label}
            sx={{
              width: GAUGE_WIDTH,
              height: GAUGE_HEIGHT,
              display: 'flex',
              alignItems: 'flex-end',
              borderRadius: 1.2,
              overflow: 'hidden',
              ...TRACK_SX,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: `${(Math.abs(row.value) / max) * 100}%`,
                bgcolor: barColor(row),
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{row.label}</Typography>
          {row.detail === '' ? null : (
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.detail}</Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

function Chart({ chart }: { chart: InsightsChart }): ReactNode {
  return (
    <ChartCard chart={chart}>
      {chart.unavailable_reason == null ? (
        chart.kind === 'bars' ? (
          <Bars chart={chart} />
        ) : chart.kind === 'share' ? (
          <Share chart={chart} />
        ) : (
          <Columns chart={chart} />
        )
      ) : (
        <Caption>{chart.unavailable_reason}</Caption>
      )}
    </ChartCard>
  );
}

// Bars run the panel's full width; the smaller charts pair up beneath them.
export function InsightsCharts({ charts }: { charts: InsightsChart[] }): ReactNode {
  if (charts.length === 0) return null;
  const wide = charts.filter((chart) => chart.kind === 'bars');
  const paired = charts.filter((chart) => chart.kind !== 'bars');
  return (
    <Stack sx={{ gap: 3 }} data-testid="insights-charts">
      {wide.map((chart) => (
        <Chart key={chart.key} chart={chart} />
      ))}
      {paired.length === 0 ? null : (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {paired.map((chart) => (
            <Chart key={chart.key} chart={chart} />
          ))}
        </Box>
      )}
    </Stack>
  );
}
